/* The Workspace Intelligence Brief — the Discovery page.

   One consistent structure for every workspace, every time:

     1. Pulse        — executive summary + freshness + live progress
     2. Headlines    — the ranked findings (LLM-narrated, stat-grounded)
     3. What moved   — recent shifts vs baseline, with sparklines
     4. Leading indicators — which signals move before which
     5. Weekly rhythms     — seasonality profiles
     6. Anomaly timeline   — outlier periods across the window
     7. Watchlist    — what deserves attention next

   Every number shown is computed deterministically by the backend
   insight engine; the LLM only narrates. While the analysis worker is
   running, the page shows live stage progress. */

import React, { useEffect, useMemo } from 'react';
import useRealtimeRefresh, { eventPrefix } from '../../realtime/useRealtimeRefresh';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Chip, IconButton, LinearProgress, Stack, Tooltip,
  Typography, alpha, useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EastIcon from '@mui/icons-material/East';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import MovingIcon from '@mui/icons-material/Moving';
import InsightsIcon from '@mui/icons-material/Insights';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import BoltIcon from '@mui/icons-material/Bolt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { DiscoveryIcon, RelationshipsIcon } from '../global/CustomIcons';

import { fetchRequest as fetchLatestAnalysis } from '../../redux/actions/signals/latestAnalysis';
import { fetchRequest as triggerAnalyze } from '../../redux/actions/signals/analyze';
import { useDisplayNames } from '../../utils/displayNames';

/* ── Formatting helpers ─────────────────────────────────────────────── */

const timeAgo = (iso) => {
  if (!iso) return null;
  // The API appends "Z" to isoformat() strings that may already carry a
  // "+00:00" offset — "...+00:00Z" parses as NaN, so strip the extra Z.
  const cleaned = String(iso).replace(/([+-]\d{2}:\d{2})Z$/, '$1');
  const t = new Date(cleaned).getTime();
  if (!Number.isFinite(t)) return null;
  const s = (Date.now() - t) / 1000;
  if (s < 90) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

const CATEGORY_META = {
  shift: { label: 'Shift', icon: MovingIcon },
  trend: { label: 'Trend', icon: TrendingUpIcon },
  lead: { label: 'Leads', icon: EastIcon },
  rhythm: { label: 'Rhythm', icon: EventRepeatIcon },
  anomaly: { label: 'Anomaly', icon: BoltIcon },
  risk: { label: 'Risk', icon: WarningAmberIcon },
};

const sevColor = (severity, theme) => ({
  critical: theme.palette.error.main,
  notable: theme.palette.warning.main,
  info: theme.palette.info.main,
}[severity] || theme.palette.info.main);

/* ── Sparkline (plain SVG — no chart deps) ──────────────────────────── */

const Spark = ({ series, width = 150, height = 34, color, recentDays = 14, theme }) => {
  const path = useMemo(() => {
    const values = series?.values || [];
    const pts = values
      .map((v, i) => ({ v, i }))
      .filter((p) => p.v != null && Number.isFinite(p.v));
    if (pts.length < 2) return null;
    const min = Math.min(...pts.map((p) => p.v));
    const max = Math.max(...pts.map((p) => p.v));
    const span = max - min || 1;
    const x = (i) => (i / (values.length - 1)) * width;
    const y = (v) => height - 3 - ((v - min) / span) * (height - 6);
    const d = pts.map((p, k) => `${k === 0 ? 'M' : 'L'}${x(p.i).toFixed(1)},${y(p.v).toFixed(1)}`).join(' ');
    const recentX = x(Math.max(0, values.length - 1 - recentDays));
    return { d, recentX, lastX: x(pts[pts.length - 1].i), lastY: y(pts[pts.length - 1].v) };
  }, [series, width, height, recentDays]);

  if (!path) return <Box sx={{ width, height }} />;
  return (
    <svg width={width} height={height}>
      <rect
        x={path.recentX} y={0} width={width - path.recentX} height={height}
        fill={alpha(color, 0.09)} rx={3}
      />
      <path d={path.d} fill="none" stroke={alpha(color, 0.85)} strokeWidth={1.4} />
      <circle cx={path.lastX} cy={path.lastY} r={2.2} fill={color} />
    </svg>
  );
};

/* ── Section shell — identical chrome for every block ───────────────── */

const Section = ({ icon: Icon, title, count, legend, children, theme }) => (
  <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
      <Icon sx={{ fontSize: 16, color: 'text.secondary' }} />
      <Typography variant="subtitle2">{title}</Typography>
      {count != null && (
        <Chip size="small" label={count} sx={{ height: 18, fontSize: 10, fontFamily: 'monospace' }} />
      )}
      {legend && (
        <>
          <Box sx={{ flex: 1 }} />
          {legend}
        </>
      )}
    </Stack>
    {children}
  </Card>
);

/* Legend explaining the shaded band on the sparklines. */
const RecentWindowLegend = ({ days, theme }) => (
  <Stack direction="row" spacing={0.6} alignItems="center">
    <Box sx={{
      width: 14, height: 10, borderRadius: 0.5,
      backgroundColor: alpha(theme.palette.text.primary, 0.1),
      border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
    }} />
    <Typography variant="caption" color="text.disabled" sx={{ whiteSpace: 'nowrap' }}>
      last {days}d vs baseline
    </Typography>
  </Stack>
);

const EmptyLine = ({ text }) => (
  <Typography variant="caption" color="text.disabled">{text}</Typography>
);

/* ── 1 · Pulse ──────────────────────────────────────────────────────── */

const Pulse = ({ brief, response, running, progress, onRefresh, theme }) => (
  <Card
    variant="outlined"
    sx={{
      p: 2.5,
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, transparent 60%)`,
    }}
  >
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5 }}>
          Executive brief
        </Typography>
        <Typography variant="body1" sx={{ mt: 0.5, maxWidth: 880, lineHeight: 1.65 }}>
          {brief?.executive_summary || 'The brief is being prepared.'}
        </Typography>
      </Box>
      <Stack spacing={0.75} alignItems={{ xs: 'flex-start', md: 'flex-end' }} sx={{ flexShrink: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {brief?.signals_analyzed ?? 0} signals · {brief?.window?.days ?? 0}d window
          </Typography>
          <Tooltip title="Re-run the analysis now" arrow>
            <span>
              <IconButton size="small" onClick={onRefresh} disabled={running}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <Typography variant="caption" color="text.disabled">
          {(() => {
            if (running) return 'updating…';
            const ago = timeAgo(response?.completed_at || brief?.computed_at);
            return ago ? `updated ${ago}` : 'not run yet';
          })()}
        </Typography>
      </Stack>
    </Stack>

    {running && (
      <Box sx={{ mt: 2 }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {progress?.label || 'Queued'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {progress?.pct ?? 0}%
          </Typography>
        </Stack>
        <LinearProgress
          variant={progress?.pct ? 'determinate' : 'indeterminate'}
          value={progress?.pct || 0}
          sx={{ height: 5, borderRadius: 3 }}
        />
      </Box>
    )}
  </Card>
);

/* ── 2 · Headlines ──────────────────────────────────────────────────── */

const HeadlineCard = ({ headline, series, theme, nameOf = (k) => k }) => {
  const color = sevColor(headline.severity, theme);
  const meta = CATEGORY_META[headline.category] || CATEGORY_META.shift;
  const Icon = meta.icon;
  const evidenceKeys = (headline.keys || []).filter((k) => series?.[k]);

  // Title + chart + chips carry the finding; the narrator's full
  // sentence lives in the hover tooltip so the card stays scannable.
  return (
    <Tooltip title={headline.detail || ''} arrow placement="top" enterDelay={300}>
      <Card
        variant="outlined"
        sx={{
          p: 1.75,
          borderLeft: `3px solid ${color}`,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
          <Icon sx={{ fontSize: 15, color }} />
          <Typography variant="caption" sx={{ color, fontWeight: 700, letterSpacing: 0.6 }}>
            {meta.label.toUpperCase()}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: color }} />
        </Stack>
        <Typography variant="subtitle2" sx={{ lineHeight: 1.4 }}>{headline.title}</Typography>
        <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
          {(headline.keys || []).map((k) => (
            <Chip key={k} size="small" label={nameOf(k)}
              sx={{ height: 18, fontSize: 10, fontFamily: 'monospace' }} />
          ))}
        </Stack>
        {evidenceKeys.length > 0 && (
          <Stack spacing={1} sx={{ mt: 1.5 }}>
            {evidenceKeys.slice(0, 3).map((k) => (
              <Stack key={k} direction="row" spacing={1.5} alignItems="center">
                <Typography noWrap variant="caption" sx={{ fontFamily: 'monospace', width: 140 }} title={k}>
                  {nameOf(k)}
                </Typography>
                <Spark series={series[k]} width={220} height={36} color={color} theme={theme} />
              </Stack>
            ))}
          </Stack>
        )}
      </Card>
    </Tooltip>
  );
};

/* ── 3 · Movers ─────────────────────────────────────────────────────── */

const MoverRow = ({ mover, series, theme, nameOf = (k) => k }) => {
  const navigate = useNavigate();
  const up = mover.direction === 'up';
  const color = up ? theme.palette.success.main : theme.palette.error.main;
  const volExtreme = mover.vol_ratio != null && (mover.vol_ratio >= 1.8 || mover.vol_ratio <= 0.55);
  return (
    <Stack
      direction="row" spacing={1.5} alignItems="center"
      sx={{
        py: 0.75,
        '&:hover .mover-investigate': { opacity: 1 },
      }}
    >
      <Typography noWrap variant="caption" sx={{ fontFamily: 'monospace', width: 150, flexShrink: 0 }} title={mover.key}>
        {nameOf(mover.key)}
      </Typography>
      <Spark series={series?.[mover.key]} color={color} theme={theme} />
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: 'auto' }}>
        {volExtreme && (
          <Tooltip title={`Volatility ×${mover.vol_ratio} vs baseline`} arrow>
            <Chip size="small" label={`σ×${mover.vol_ratio.toFixed(1)}`}
              sx={{
                height: 18, fontSize: 10, fontFamily: 'monospace',
                color: theme.palette.warning.main,
                backgroundColor: alpha(theme.palette.warning.main, 0.12),
              }} />
          </Tooltip>
        )}
        <Tooltip title={`${Math.abs(mover.shift_z).toFixed(1)}σ ${mover.direction} vs its baseline`} arrow>
          <Chip
            size="small"
            icon={up
              ? <TrendingUpIcon sx={{ fontSize: 13 }} />
              : <TrendingDownIcon sx={{ fontSize: 13 }} />}
            label={mover.pct_change != null
              ? `${mover.pct_change > 0 ? '+' : ''}${mover.pct_change.toFixed(1)}%`
              : `${mover.shift_z > 0 ? '+' : ''}${mover.shift_z.toFixed(1)}σ`}
            sx={{
              height: 20, fontSize: 11, fontFamily: 'monospace', fontWeight: 700,
              color, backgroundColor: alpha(color, 0.12),
              '& .MuiChip-icon': { color },
            }}
          />
        </Tooltip>
        <Tooltip title="Investigate what drives this signal (creates a Goal)" arrow>
          <IconButton
            className="mover-investigate"
            size="small"
            onClick={() => navigate(`/goals/create?target=${encodeURIComponent(mover.key)}`)}
            sx={{ opacity: 0, transition: 'opacity 120ms ease', p: 0.25 }}
          >
            <DiscoveryIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
};

/* ── 4 · Leading indicators ─────────────────────────────────────────── */

/* One row per signal-family relationship. The backend collapses
   co-moving signals (e.g. OHLC of one instrument) into a single claim;
   extra members show as a "+N" chip with the full list on hover. */
const FamilyName = ({ name, members, theme, nameOf = (k) => k }) => {
  const extra = (members || []).filter((m) => m !== name);
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
      <Typography noWrap variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }} title={name}>
        {nameOf(name)}
      </Typography>
      {extra.length > 0 && (
        <Tooltip title={`Co-moving: ${extra.map(nameOf).join(', ')}`} arrow>
          <Chip size="small" label={`+${extra.length}`}
            sx={{
              height: 15, fontSize: 9, fontFamily: 'monospace',
              color: theme.palette.text.secondary,
              backgroundColor: alpha(theme.palette.text.secondary, 0.12),
            }} />
        </Tooltip>
      )}
    </Stack>
  );
};

const LeaderRow = ({ leader, theme, nameOf = (k) => k }) => {
  const navigate = useNavigate();
  const color = leader.sign === 'positive' ? theme.palette.success.main : theme.palette.error.main;
  return (
    <Box sx={{ py: 0.75, '&:hover .leader-link': { opacity: 1 } }}>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <FamilyName name={leader.source} members={leader.sources} theme={theme} nameOf={nameOf} />
        <Chip
          size="small"
          icon={<EastIcon sx={{ fontSize: 12 }} />}
          label={`${leader.lag_days}d`}
          sx={{
            height: 17, fontSize: 10, fontFamily: 'monospace',
            color: theme.palette.info.main,
            backgroundColor: alpha(theme.palette.info.main, 0.1),
            '& .MuiChip-icon': { color: theme.palette.info.main },
          }}
        />
        <FamilyName name={leader.target} members={leader.targets} theme={theme} nameOf={nameOf} />
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" sx={{ fontFamily: 'monospace', color }}>
          r={leader.corr > 0 ? '+' : ''}{leader.corr.toFixed(2)}
        </Typography>
        <Tooltip title="Explore in the Relationships graph" arrow>
          <IconButton
            className="leader-link"
            size="small"
            onClick={() => navigate('/relationships')}
            sx={{ opacity: 0, transition: 'opacity 120ms ease', p: 0.25 }}
          >
            <RelationshipsIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Stack>
      <Box sx={{ mt: 0.5, height: 3, borderRadius: 2, backgroundColor: alpha(theme.palette.divider, 0.4) }}>
        <Box sx={{
          width: `${Math.min(100, leader.strength * 100)}%`,
          height: '100%', borderRadius: 2, backgroundColor: alpha(color, 0.75),
        }} />
      </Box>
    </Box>
  );
};

/* ── 5 · Weekly rhythms ─────────────────────────────────────────────── */

const DAY_LETTERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const RhythmRow = ({ rhythm, theme, nameOf = (k) => k }) => {
  const maxAbs = Math.max(...rhythm.weekday_profile.map((v) => Math.abs(v)), 0.001);
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.75 }}>
      <Typography noWrap variant="caption" sx={{ fontFamily: 'monospace', width: 150, flexShrink: 0 }} title={rhythm.key}>
        {nameOf(rhythm.key)}
      </Typography>
      <Stack direction="row" spacing={0.4}>
        {rhythm.weekday_profile.map((v, i) => {
          const pos = v >= 0;
          const c = pos ? theme.palette.success.main : theme.palette.error.main;
          return (
            <Tooltip key={i} title={`${['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i]} · ${v > 0 ? '+' : ''}${v.toFixed(2)}σ`} arrow>
              <Box sx={{
                width: 20, height: 20, borderRadius: 0.5,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: alpha(c, 0.12 + 0.55 * (Math.abs(v) / maxAbs)),
                border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
              }}>
                <Typography sx={{ fontSize: 9, color: 'text.secondary', fontFamily: 'monospace' }}>
                  {DAY_LETTERS[i]}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto !important', whiteSpace: 'nowrap' }}>
        peaks {rhythm.peak.slice(0, 3)}
      </Typography>
    </Stack>
  );
};

/* ── 6 · Anomaly timeline ───────────────────────────────────────────── */

const ANOMALY_KIND_META = {
  spike: { label: 'spike' },
  drop: { label: 'drop' },
  regime_change: { label: 'regime change' },
};

const AnomalyTimeline = ({ anomalies, window, theme, nameOf = (k) => k }) => {
  const width = 560;
  const laneH = 16;
  const start = new Date(`${window?.start}T00:00:00Z`).getTime();
  const end = new Date(`${window?.end}T00:00:00Z`).getTime();
  const span = Math.max(1, end - start);
  const keys = [...new Set(anomalies.map((a) => a.key))];

  const colorFor = (kind) => ({
    spike: theme.palette.warning.main,
    drop: theme.palette.error.main,
    regime_change: theme.palette.secondary.main,
  }[kind] || theme.palette.warning.main);

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <svg width="100%" height={keys.length * laneH + 26} viewBox={`0 0 ${width} ${keys.length * laneH + 26}`} preserveAspectRatio="none" style={{ minWidth: 360 }}>
        {keys.map((k, lane) => (
          <line key={k} x1={0} x2={width} y1={lane * laneH + 8} y2={lane * laneH + 8}
            stroke={alpha(theme.palette.divider, 0.35)} strokeWidth={1} />
        ))}
        {anomalies.map((a, i) => {
          const lane = keys.indexOf(a.key);
          const x1 = ((new Date(`${a.date_start}T00:00:00Z`).getTime() - start) / span) * width;
          const x2 = ((new Date(`${a.date_end}T00:00:00Z`).getTime() - start) / span) * width;
          const c = colorFor(a.kind);
          return (
            <Tooltip
              key={i}
              title={`${nameOf(a.key)} · ${(ANOMALY_KIND_META[a.kind] || {}).label || a.kind} · ${a.date_start}${a.date_end !== a.date_start ? ` → ${a.date_end}` : ''} · ${a.z > 0 ? '+' : ''}${a.z}σ`}
              arrow
            >
              <g>
                <rect
                  x={Math.max(0, x1 - 2)} y={lane * laneH + 3}
                  width={Math.max(6, x2 - x1 + 4)} height={10} rx={3}
                  fill={alpha(c, 0.35 + 0.5 * a.severity)}
                  stroke={c} strokeWidth={0.75}
                />
              </g>
            </Tooltip>
          );
        })}
        <text x={0} y={keys.length * laneH + 20} fontSize={9} fill={theme.palette.text.disabled} fontFamily="monospace">
          {window?.start}
        </text>
        <text x={width} y={keys.length * laneH + 20} fontSize={9} fill={theme.palette.text.disabled} textAnchor="end" fontFamily="monospace">
          {window?.end}
        </text>
      </svg>
      <Stack spacing={0.25} sx={{ mt: 0.5 }}>
        {keys.map((k) => {
          const worst = anomalies.filter((a) => a.key === k)
            .sort((a, b) => b.severity - a.severity)[0];
          return (
            <Stack key={k} direction="row" spacing={1} alignItems="center">
              <Box sx={{
                width: 8, height: 8, borderRadius: 0.5, flexShrink: 0,
                backgroundColor: colorFor(worst.kind),
              }} />
              <Typography noWrap variant="caption" sx={{ fontFamily: 'monospace' }} title={k}>{nameOf(k)}</Typography>
              <Typography variant="caption" color="text.disabled">
                {(ANOMALY_KIND_META[worst.kind] || {}).label || worst.kind} · {worst.date_end}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
};

/* ── 7 · Watchlist ──────────────────────────────────────────────────── */

const WatchRow = ({ item, series, theme, nameOf = (k) => k }) => (
  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.75 }}>
    <Typography noWrap variant="caption" sx={{ fontFamily: 'monospace', width: 150, flexShrink: 0 }} title={item.key}>
      {nameOf(item.key)}
    </Typography>
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, flex: 1 }}>
      {item.reasons.map((r) => (
        <Chip key={r} size="small" label={r}
          sx={{
            height: 18, fontSize: 10,
            color: theme.palette.warning.main,
            backgroundColor: alpha(theme.palette.warning.main, 0.1),
          }} />
      ))}
    </Stack>
    {series?.[item.key] && (
      <Spark series={series[item.key]} width={110} height={26}
        color={theme.palette.warning.main} theme={theme} />
    )}
  </Stack>
);

/* ── Full-page states ───────────────────────────────────────────────── */

const HeroState = ({ icon, title, body, children }) => (
  <Card variant="outlined">
    <Stack alignItems="center" spacing={1.5} sx={{ py: 8, px: 3, textAlign: 'center' }}>
      {icon}
      <Typography variant="h6">{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460 }}>
        {body}
      </Typography>
      {children}
    </Stack>
  </Card>
);

/* ── Main ───────────────────────────────────────────────────────────── */

const RUNNING_STATUSES = new Set(['queued', 'processing']);

const InsightsBrief = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const response = useSelector((s) => s.signal_latest_analysis?.response);
  // Friendly display names for raw signal keys ("o" -> "Open").
  const nameOf = useDisplayNames();

  const status = response?.status;
  const brief = response?.results?.brief;
  const running = RUNNING_STATUSES.has(status)
    || RUNNING_STATUSES.has(response?.pending_status)
    || Boolean(response?.refresh_in_progress);
  const progress = response?.pending_progress;

  useEffect(() => {
    dispatch(fetchLatestAnalysis({}));
  }, [dispatch]);

  // Live (no polling): the workspace-analysis worker emits
  // `signal.analysis.<status>` on the workspace channel as it progresses —
  // refetch the brief when one arrives (and on reconnect). Replaces the 1.5s
  // poll.
  useRealtimeRefresh(
    eventPrefix('signal.analysis.'),
    () => dispatch(fetchLatestAnalysis({ skip_refresh: true })),
  );

  const onRefresh = () => dispatch(triggerAnalyze({ force: true }));

  if (status === 'insufficient_data') {
    return (
      <HeroState
        icon={<InsightsIcon sx={{ fontSize: 40, color: 'text.disabled' }} />}
        title="Not enough data yet"
        body="Import data first — once your workspace has at least 10 data points, Discovery builds an intelligence brief over every signal."
      />
    );
  }

  // First-ever run: no cached brief to show — full progress hero.
  if (!brief && running) {
    return (
      <HeroState
        icon={<InsightsIcon sx={{ fontSize: 40, color: 'primary.main' }} />}
        title="Building your intelligence brief"
        body={progress?.label || 'Scanning every signal for shifts, lead-lag relationships, rhythms and anomalies.'}
      >
        <Box sx={{ width: 320 }}>
          <LinearProgress
            variant={progress?.pct ? 'determinate' : 'indeterminate'}
            value={progress?.pct || 0}
            sx={{ height: 5, borderRadius: 3 }}
          />
          {progress?.pct != null && (
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {progress.pct}%
            </Typography>
          )}
        </Box>
      </HeroState>
    );
  }

  // Analysis exists but predates the brief format — needs one fresh run.
  if (!brief) {
    return (
      <HeroState
        icon={<InsightsIcon sx={{ fontSize: 40, color: 'text.disabled' }} />}
        title="Your brief is one run away"
        body="This workspace's analysis predates the intelligence brief. Run it once to map movers, leading indicators, rhythms and anomalies."
      >
        <Chip
          icon={<RefreshIcon />}
          label="Run analysis"
          onClick={onRefresh}
          color="primary"
          sx={{ mt: 1 }}
        />
      </HeroState>
    );
  }

  const series = brief.series || {};
  const headlines = brief.headlines || [];
  const movers = brief.movers || [];
  const leaders = brief.leaders || [];
  const rhythms = brief.rhythms || [];
  const anomalies = brief.anomalies || [];
  const watchlist = brief.watchlist || [];
  const stale = brief.health?.stale || [];

  return (
    <Stack spacing={2}>
      <Pulse
        brief={brief} response={response} running={running}
        progress={progress} onRefresh={onRefresh} theme={theme}
      />

      {headlines.length > 0 && (
        <Box sx={{
          display: 'grid', gap: 1.5,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
        }}>
          {headlines.map((h, i) => (
            <HeadlineCard key={i} headline={h} series={series} theme={theme} nameOf={nameOf} />
          ))}
        </Box>
      )}

      <Box sx={{
        display: 'grid', gap: 2,
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
      }}>
        <Section
          icon={MovingIcon} title="What moved" count={movers.length}
          legend={(
            <RecentWindowLegend
              days={brief.window?.recent_days || 14}
              theme={theme}
            />
          )}
          theme={theme}
        >
          {movers.length === 0
            ? <EmptyLine text="No significant level or volatility shifts in the last two weeks." />
            : movers.map((m) => (
              <MoverRow key={m.key} mover={m} series={series} theme={theme} nameOf={nameOf} />
            ))}
        </Section>

        <Section icon={EastIcon} title="Leading indicators" count={leaders.length} theme={theme}>
          {leaders.length === 0
            ? <EmptyLine text="No signal reliably moves ahead of another in this window." />
            : leaders.map((l) => (
              <LeaderRow key={`${l.source}>${l.target}`} leader={l} theme={theme} nameOf={nameOf} />
            ))}
        </Section>

        <Section icon={EventRepeatIcon} title="Weekly rhythms" count={rhythms.length} theme={theme}>
          {rhythms.length === 0
            ? <EmptyLine text="No weekly seasonality strong enough to act on." />
            : rhythms.map((r) => (
              <RhythmRow key={r.key} rhythm={r} theme={theme} nameOf={nameOf} />
            ))}
        </Section>

        <Section icon={BoltIcon} title="Anomaly timeline" count={anomalies.length} theme={theme}>
          {anomalies.length === 0
            ? <EmptyLine text="No outlier periods in the last 90 days." />
            : <AnomalyTimeline anomalies={anomalies} window={brief.window} theme={theme} nameOf={nameOf} />}
        </Section>
      </Box>

      <Section icon={VisibilityIcon} title="Watchlist" count={watchlist.length + stale.length} theme={theme}>
        {watchlist.length === 0 && stale.length === 0
          ? <EmptyLine text="Nothing needs your attention — all signals fresh and within their normal ranges." />
          : (
            <>
              {watchlist.map((w) => (
                <WatchRow key={w.key} item={w} series={series} theme={theme} nameOf={nameOf} />
              ))}
              {stale.filter((s) => !watchlist.some((w) => w.key === s.key)).map((s) => (
                <WatchRow
                  key={s.key}
                  item={{ key: s.key, reasons: [`silent for ${s.days_silent}d`] }}
                  series={series}
                  theme={theme}
                  nameOf={nameOf}
                />
              ))}
            </>
          )}
      </Section>
    </Stack>
  );
};

export default InsightsBrief;
